import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export type AuthStatus = 'init' | 'anonymous' | 'authenticated' | 'error';

interface AuthState {
  status: AuthStatus;
  session: Session | null;
  user: User | null;
  errorMessage: string | null;

  init: () => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  signUpWithPassword: (email: string, password: string) => Promise<{ ok: boolean; error?: string; needsConfirmation?: boolean }>;
  signInWithGoogle: () => Promise<{ ok: boolean; error?: string }>;
  resetPassword: (email: string) => Promise<{ ok: boolean; error?: string }>;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  status: 'init',
  session: null,
  user: null,
  errorMessage: null,

  init: async () => {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      set({ status: 'error', errorMessage: error.message });
      return;
    }
    if (data.session) {
      set({ status: 'authenticated', session: data.session, user: data.session.user });
    } else {
      set({ status: 'anonymous', session: null, user: null });
    }

    // Subscribe to subsequent auth changes
    supabase.auth.onAuthStateChange((_event, session) => {
      const current = get();
      if (session) {
        if (current.session?.access_token !== session.access_token) {
          set({ status: 'authenticated', session, user: session.user });
        }
      } else if (current.status !== 'anonymous') {
        set({ status: 'anonymous', session: null, user: null });
      }
    });
  },

  signInWithPassword: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      set({ errorMessage: error.message });
      return { ok: false, error: error.message };
    }
    set({ status: 'authenticated', session: data.session, user: data.user, errorMessage: null });
    return { ok: true };
  },

  signUpWithPassword: async (email, password) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) {
      set({ errorMessage: error.message });
      return { ok: false, error: error.message };
    }
    // If email confirmation is on, session will be null until user clicks the link
    if (data.session) {
      set({ status: 'authenticated', session: data.session, user: data.user, errorMessage: null });
      return { ok: true };
    }
    return { ok: true, needsConfirmation: true };
  },

  signInWithGoogle: async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: typeof window !== 'undefined' ? window.location.origin : undefined,
      },
    });
    if (error) {
      set({ errorMessage: error.message });
      return { ok: false, error: error.message };
    }
    return { ok: true };
  },

  resetPassword: async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: typeof window !== 'undefined' ? `${window.location.origin}/reset-password` : undefined,
    });
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  },

  signOut: async () => {
    await supabase.auth.signOut();
    set({ status: 'anonymous', session: null, user: null, errorMessage: null });
  },
}));
